import React, { useMemo } from 'react';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';
import type { Employee, HiringProcess } from '@smartphone-tycoon/shared-core';
import { employeeService, EMPLOYEE_ROLES } from '../../../services/tycoon/employeeService';
import { GAME_CONSTANTS } from '../../../config/gameConstants';
import type { OwnedBuilding } from '@/types/buildings';
import { buildingService } from '@/services/tycoon/buildingService';

interface OperationsCostsProps {
  employees: Employee[];
  hiringProcesses?: HiringProcess[];
  cash: number;
  buildings?: OwnedBuilding[];
}

export function OperationsCosts({ employees, hiringProcesses = [], buildings = [] }: OperationsCostsProps) {
  // Calculate detailed employee costs
  const employeeCostBreakdown = useMemo(() => {
    const dailySalaryCost = employeeService.calculateDailySalaryCosts(employees);
    const monthlySalaryCost = dailySalaryCost * 30;
    const stats = employeeService.getEmployeeStats(employees);
    
    // Calculate costs by role dynamically
    const costsByRole: Record<string, number> = {};
    const monthlyCostsByRole: Record<string, number> = {};
    
    Object.keys(EMPLOYEE_ROLES).forEach(roleKey => {
      costsByRole[roleKey] = employees
        .filter(e => e.role.toLowerCase() === roleKey)
        .reduce((sum, e) => sum + e.salary, 0);
      monthlyCostsByRole[roleKey] = costsByRole[roleKey] * 30;
    });
    
    return {
      dailySalaryCost,
      monthlySalaryCost,
      stats,
      costsByRole,
      monthlyCostsByRole
    };
  }, [employees]);

  // Calculate hiring costs
  const hiringCosts = useMemo(() => {
    return hiringProcesses.map(process => {
      const role = EMPLOYEE_ROLES[process.role.toLowerCase()];
      const dailySalary = role ? role.baseSalary : 0;
      const totalCost = dailySalary * process.count * 30; // Monthly cost
      return {
        ...process,
        monthlyCost: totalCost,
        dailyCost: dailySalary * process.count
      };
    });
  }, [hiringProcesses]);

  // Calculate overhead costs
  const overheadCosts = useMemo(() => {
    const baseOperatingCost = GAME_CONSTANTS.finance.baseOperatingCosts;
    const officeSpace = Math.ceil(employees.length / 10) * 5000; // TODO: Make office cost configurable
    const utilities = employees.length * 100; // TODO: Make utility cost configurable
    const equipment = employees.length * 200; // TODO: Make equipment cost configurable
    const insurance = employees.length * 150; // TODO: Make insurance cost configurable
    
    return {
      baseOperating: baseOperatingCost,
      officeSpace,
      utilities,
      equipment,
      insurance,
      totalDaily: baseOperatingCost + (officeSpace + utilities + equipment + insurance) / 30,
      totalMonthly: baseOperatingCost * 30 + officeSpace + utilities + equipment + insurance
    };
  }, [employees.length]);

  // Calculate building costs
  const buildingCosts = useMemo(() => {
    if (!buildings || buildings.length === 0) {
      return { totalMaintenance: 0, byBuilding: [] };
    }
    return buildingService.calculateMaintenanceCosts(buildings);
  }, [buildings]);

  // Total operations costs
  const totalCosts = useMemo(() => {
    const totalHiringMonthly = hiringCosts.reduce((sum, h) => sum + h.monthlyCost, 0);
    const buildingMaintenanceDaily = buildingCosts.totalMaintenance / 30;
    return {
      daily: employeeCostBreakdown.dailySalaryCost + overheadCosts.totalDaily + buildingMaintenanceDaily,
      monthly: employeeCostBreakdown.monthlySalaryCost + overheadCosts.totalMonthly + totalHiringMonthly + buildingCosts.totalMaintenance
    };
  }, [employeeCostBreakdown, overheadCosts, hiringCosts, buildingCosts]);

  return (
    <div className="space-y-6">
      {/* Total Summary */}
      <div className="bg-theme-bg-tertiary text-theme-text-primary rounded-lg shadow">
        <div className="p-6">
          <h3 className="text-xl font-bold mb-4">Total Operations Costs</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-theme-text-muted text-sm">Daily Cost</p>
              <p className="text-2xl font-bold">{formatCurrency(totalCosts.daily)}</p>
            </div>
            <div>
              <p className="text-theme-text-muted text-sm">Monthly Cost</p>
              <p className="text-2xl font-bold">{formatCurrency(totalCosts.monthly)}</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-theme-border">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-theme-text-muted">Employee Salaries</p>
                <p className="font-medium">{formatCurrency(employeeCostBreakdown.monthlySalaryCost)}</p>
              </div>
              <div>
                <p className="text-theme-text-muted">Overhead</p>
                <p className="font-medium">{formatCurrency(overheadCosts.totalMonthly)}</p>
              </div>
              <div>
                <p className="text-theme-text-muted">Buildings</p>
                <p className="font-medium">{formatCurrency(buildingCosts.totalMaintenance)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Employee Costs by Role */}
      <div className="bg-theme-bg-content rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold text-theme-text-primary">Employee Costs by Role</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-theme-border">
            <thead className="bg-theme-bg-secondary">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                  Role
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                  Count
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                  Daily Cost/Employee
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                  Total Daily
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                  Total Monthly
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                  % of Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-theme-bg-content divide-y divide-theme-border">
              {Object.entries(EMPLOYEE_ROLES).map(([key, role]) => {
                const count = employeeCostBreakdown.stats[key] || 0;
                const dailyCost = employeeCostBreakdown.costsByRole[key] || 0;
                const monthlyCost = employeeCostBreakdown.monthlyCostsByRole[key] || 0;
                const percentage = employeeCostBreakdown.monthlySalaryCost > 0 
                  ? (monthlyCost / employeeCostBreakdown.monthlySalaryCost) * 100 
                  : 0;
                
                return (
                  <tr key={key}>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-theme-text-primary">{role.name}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-theme-text-primary">
                      {count}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-theme-text-primary">
                      {formatCurrency(role.baseSalary)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-theme-text-primary">
                      {formatCurrency(dailyCost)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium text-theme-text-primary">
                      {formatCurrency(monthlyCost)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-theme-text-primary">
                      {percentage.toFixed(1)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-theme-bg-secondary">
              <tr>
                <td className="px-4 py-3 font-medium text-theme-text-primary">
                  Total
                </td>
                <td className="px-4 py-3 text-right font-bold text-theme-text-primary">
                  {employees.length}
                </td>
                <td></td>
                <td className="px-4 py-3 text-right font-bold text-theme-text-primary">
                  {formatCurrency(employeeCostBreakdown.dailySalaryCost)}
                </td>
                <td className="px-4 py-3 text-right font-bold text-theme-text-primary">
                  {formatCurrency(employeeCostBreakdown.monthlySalaryCost)}
                </td>
                <td className="px-4 py-3 text-right font-bold text-theme-text-primary">
                  100%
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Hiring Pipeline Costs */}
      {hiringProcesses.length > 0 && (
        <div className="bg-theme-bg-content rounded-lg shadow">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold text-theme-text-primary">Hiring Pipeline Costs</h3>
          </div>
          <div className="p-4">
            <div className="space-y-3">
              {hiringCosts.map((hire, index) => (
                <div key={index} className="flex justify-between items-center py-2 border-b">
                  <div>
                    <p className="font-medium text-theme-text-primary">
                      {hire.count} {hire.role}{hire.count > 1 ? 's' : ''}
                    </p>
                    <p className="text-sm text-theme-text-muted">
                      {hire.daysRemaining} days remaining
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-theme-text-primary">
                      {formatCurrency(hire.monthlyCost)}/month
                    </p>
                    <p className="text-sm text-theme-text-muted">
                      Once hired
                    </p>
                  </div>
                </div>
              ))}
              <div className="flex justify-between items-center py-2 font-bold">
                <p>Total Monthly Cost (once hired)</p>
                <p>{formatCurrency(hiringCosts.reduce((sum, h) => sum + h.monthlyCost, 0))}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Building Maintenance Costs */}
      {buildings && buildings.length > 0 && (
        <div className="bg-theme-bg-content rounded-lg shadow">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold text-theme-text-primary">Building Maintenance Costs</h3>
          </div>
          <div className="p-4">
            <div className="space-y-3">
              {buildingCosts.byBuilding.map((building, index) => (
                <div key={index} className="flex justify-between items-center py-2 border-b">
                  <div>
                    <p className="font-medium text-theme-text-primary">{building.buildingName}</p>
                  </div>
                  <p className="font-medium text-theme-text-primary">{formatCurrency(building.maintenanceCost)}/month</p>
                </div>
              ))}
              <div className="flex justify-between items-center py-2 font-bold">
                <p>Total Building Maintenance</p>
                <p>{formatCurrency(buildingCosts.totalMaintenance)}/month</p>
              </div>
            </div>
          </div>
        </div>
      )}

     
    </div>
  );
}